import React from 'react';

import {
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
} from '@mui/material';

import type { BaseTextInputProperties } from './BaseTextInput.types';

/**
 * Базовый текстовый инпут на основе composed MUI компонентов.
 * Label рендерится через InputLabel с shrink — всегда над полем.
 * OutlinedInput с notched={false} — чистый border без выреза.
 *
 * Поддерживает два стиля передачи props:
 * 1. Прямые props (name, value, onChange, slotProps.input)
 * 2. TextField-style (InputProps, inputProps) — для совместимости с Autocomplete
 */
export const BaseTextInput = React.forwardRef<
  HTMLDivElement,
  BaseTextInputProperties
>(
  (
    {
      dataTest,
      label,
      id,
      error,
      disabled,
      placeholder,
      helperText,
      sx = { width: '100%' },
      required,
      size,
      slotProps,
      // TextField-style props (from Autocomplete renderInput)
      InputProps,
      inputProps: textFieldInputProperties,
      ...outlinedInputProperties
    }: BaseTextInputProperties,
    reference,
  ) => {
    // Merge slotProps.input and InputProps into OutlinedInput direct props
    const {
      ref: slotReference,
      sx: slotSx,
      startAdornment: slotStartAdornment,
      endAdornment: slotEndAdornment,
    } = slotProps?.input ?? {};

    const {
      ref: autocompleteReference,
      className: autocompleteClassName,
      startAdornment: autocompleteStartAdornment,
      endAdornment: autocompleteEndAdornment,
    } = InputProps ?? {};

    const mergedInputProperties = {
      ...textFieldInputProperties,
      ...slotProps?.htmlInput,
    };

    return (
      <FormControl
        fullWidth
        error={error}
        disabled={disabled}
        required={required}
        sx={sx}
        ref={reference}
        data-testid={dataTest}
      >
        {label && (
          <InputLabel
            htmlFor={id}
            shrink
            sx={{
              position: 'relative',
              transform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 400,
              mb: 0.75,
              '&.Mui-focused': {
                color: error ? 'error.main' : 'text.secondary',
              },
            }}
          >
            {label}
          </InputLabel>
        )}
        <OutlinedInput
          id={id}
          placeholder={placeholder ?? (typeof label === 'string' ? label : undefined)}
          notched={false}
          label={undefined}
          size={size}
          ref={slotReference ?? autocompleteReference}
          sx={slotSx}
          startAdornment={slotStartAdornment ?? autocompleteStartAdornment}
          endAdornment={slotEndAdornment ?? autocompleteEndAdornment}
          className={autocompleteClassName}
          inputProps={mergedInputProperties}
          {...outlinedInputProperties}
        />
        {helperText && (
          <FormHelperText>{helperText}</FormHelperText>
        )}
      </FormControl>
    );
  },
);

BaseTextInput.displayName = 'BaseTextInput';
